import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { TimUnreadMessageItem } from './tim-bridge';
import { formatTimToastTime, getTimToastText } from './tim-toast-text';
import './TimUnreadToast.css';

interface Props {
  peers: TimUnreadMessageItem[];
  role: 'brand' | 'creator';
  language: string;
  onOpen: (item?: TimUnreadMessageItem) => void;
  onDismiss: () => void;
  onRender5sAck?: (items: TimUnreadMessageItem[]) => void;
}

function Avatar({ item }: { item: TimUnreadMessageItem }) {
  const [failedUrl, setFailedUrl] = useState('');
  return (
    <span className="tim-toast-avatar keep-px">
      {item.peer.avatarUrl && item.peer.avatarUrl !== failedUrl ? (
        <img
          src={item.peer.avatarUrl}
          alt=""
          onError={() => setFailedUrl(item.peer.avatarUrl || '')}
        />
      ) : (
        <span>{Array.from(item.peer.name)[0]?.toUpperCase() || '?'}</span>
      )}
    </span>
  );
}

export function TimUnreadToast({
  peers,
  role,
  language,
  onOpen,
  onDismiss,
  onRender5sAck
}: Props) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  const cancelPresentation = useRef(() => {});
  const shownCallback = useRef(onRender5sAck);
  shownCallback.current = onRender5sAck;
  const shownItems = useRef(peers.slice(0, 3));
  shownItems.current = peers.slice(0, 3);
  const shownKey = JSON.stringify([language, peers.slice(0, 3).map((item) => [item.timGroupId, item.message.sequence, item.message.id, item.message.preview, item.unreadCount, item.peer, item.campaignName])]);
  useEffect(() => {
    // ACK requires five uninterrupted foreground seconds after DOM commit.
    let timer: number | undefined;
    let frame: number | undefined;
    let acknowledged = false;
    let ended = false;
    const cancel = () => {
      if (frame !== undefined) window.cancelAnimationFrame(frame);
      if (timer !== undefined) window.clearTimeout(timer);
      frame = undefined;
      timer = undefined;
    };
    const end = () => { ended = true; cancel(); };
    cancelPresentation.current = end;
    const start = () => {
      cancel();
      if (document.visibilityState !== 'visible') { end(); return; }
      if (ended || acknowledged || document.visibilityState !== 'visible') return;
      frame = window.requestAnimationFrame(() => {
        if (ended || document.visibilityState !== 'visible') return;
        timer = window.setTimeout(() => {
          if (ended || document.visibilityState !== 'visible') return;
          acknowledged = true;
          shownCallback.current?.(shownItems.current);
        }, 5000);
      });
    };
    document.addEventListener('visibilitychange', start);
    window.addEventListener('pagehide', end);
    start();
    return () => {
      cancel();
      document.removeEventListener('visibilitychange', start);
      window.removeEventListener('pagehide', end);
    };
  }, [shownKey]);
  if (!peers.length) return null;
  const text = getTimToastText(language);
  const latest = peers[0];
  const total = peers.reduce((count, item) => count + item.unreadCount, 0);
  const single = peers.length === 1 && total === 1;
  const time = formatTimToastTime(latest.message.sentAt, now, language);

  return createPortal(
    <aside
      className={`tim-unread-toast tim-toast-${role} tim-toast-${
        single ? 'single-state' : 'aggregate-state'
      } keep-px`}
      data-testid="tim-unread-toast"
      aria-label={single ? latest.peer.name : text.title(total)}
    >
      <button
        type="button"
        className="tim-toast-close keep-px"
        onClick={() => { cancelPresentation.current(); onDismiss(); }}
        aria-label={text.close}
      >
        ×
      </button>
      {single ? (
        <div className="tim-toast-single keep-px">
          <Avatar item={latest} />
          <div className="tim-toast-content keep-px">
            <div className="tim-toast-heading keep-px">
              <strong title={latest.peer.name}>{latest.peer.name}</strong>
              {latest.campaignName && (
                <span title={latest.campaignName}>{latest.campaignName}</span>
              )}
            </div>
            <div className="tim-toast-time keep-px">{time}</div>
            <p className="tim-toast-preview keep-px">
              <span>{latest.message.preview}</span>
            </p>
            <div className="tim-toast-actions keep-px">
              <button
                type="button"
                className="tim-toast-primary keep-px"
                onClick={() => { cancelPresentation.current(); onOpen(latest); }}
              >
                {text.reply}
              </button>
              <button type="button" onClick={() => { cancelPresentation.current(); onDismiss(); }}>
                {text.dismiss}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="tim-toast-title keep-px" role="status">
            {text.title(total)}
          </div>
          <div className="tim-toast-time keep-px">{time}</div>
          <div className="tim-toast-rows keep-px">
            {peers.slice(0, 3).map((item) => (
              <button
                type="button"
                className="tim-toast-row keep-px"
                key={item.peer.id}
                onClick={() => { cancelPresentation.current(); onOpen(item); }}
                data-testid="tim-toast-row"
              >
                <Avatar item={item} />
                <span className="tim-toast-row-text keep-px">
                  <strong>{item.peer.name}</strong>
                  <span>{item.message.preview}</span>
                </span>
                <span
                  className="tim-toast-badge keep-px"
                  aria-label={String(item.unreadCount)}
                >
                  {item.unreadCount > 99 ? '99+' : item.unreadCount}
                </span>
                <span className="tim-toast-chevron keep-px" aria-hidden="true">
                  ›
                </span>
              </button>
            ))}
          </div>
          {peers.length > 3 && (
            <button
              type="button"
              className="tim-toast-more keep-px"
              onClick={() => { cancelPresentation.current(); onOpen(); }}
            >
              {text.more(peers.length - 3, role === 'creator')}
            </button>
          )}
          <div className="tim-toast-actions keep-px">
            <button
              type="button"
              className="tim-toast-primary keep-px"
              onClick={() => { cancelPresentation.current(); onOpen(); }}
            >
              {text.view}
            </button>
            <button type="button" onClick={() => { cancelPresentation.current(); onDismiss(); }}>
              {text.dismiss}
            </button>
          </div>
        </>
      )}
    </aside>,
    document.body
  );
}
